/**
 * Event → animation. Effects are cosmetic only: they never move a sprite off the spot its track
 * gives it (the jostle is a 1 px wobble around it) and never change what happens. Under
 * prefers-reduced-motion there is no shake, hit-stop, flash, squash, or wobble; labels, banners
 * and end states (a pancaked rusher down, a tackler knocked over, and back up when his track moves
 * him again) still show, because they carry information. A skipped event is played `quiet`: end
 * states only.
 */
import Phaser from 'phaser';
import type { PlayEvent, UnitId } from '@tdmons/sim';
import { color, cssColor, num } from '../ui/tokens';
import { floatsFor, type Float, type FloatKind } from './floats';
import { labelAnchor, tagAbove } from './geometry';
import type { UnitSprite } from './sprites';

export interface FxHost {
  scene: Phaser.Scene;
  sprites: ReadonlyMap<UnitId, UnitSprite>;
  reduced: boolean;
  /** Device pixels per CSS pixel: fixed CSS sizes (text, strokes, shake) are multiplied by it. */
  dpr: number;
  textures: { ring: string; spark: string };
  /** Device px of the canvas's bottom the scorebug covers: tags flip above it (slice 8d). */
  safeInset(): number;
  toPx(spot: [number, number]): { x: number; y: number };
  moveName(unit: UnitId, moveId: string): string;
  hitStop(ms: number): void;
  /** A one-word call over the field: SACK, INTERCEPTED (HTML, not Phaser). */
  banner(text: string): void;
  /** Dim the ball where it lies (an incomplete pass). */
  deadBall(): void;
  onWhistle(e: Extract<PlayEvent, { type: 'whistle' }>): void;
}

/** How long a label or marker stays up, in ms. Presentation only. */
const LABEL_MS = 700;
const POP_MS = 120;
const RING_MS = 320;
const FRAME_MS = 1000 / 60;
/** Move tags, in CSS px (Press Start 2P stays at 9-11 px). */
const LABEL_CSS_PX = 9;
const LABEL_STROKE_CSS_PX = 3;
const LABEL_GAP_CSS_PX = 2;
const LABEL_RISE_CSS_PX = 8;
const LINK_CSS_PX = 2;
/** Each floating number's color token. */
const FLOAT_COLOR: Record<FloatKind, string> = {
  damage: 'float-damage',
  heal: 'float-heal',
  'mult-up': 'float-up',
  'mult-down': 'float-down',
  crit: 'float-crit',
};

export class Fx {
  private engaged: { a: UnitId; b: UnitId; until: number }[] = [];
  /** Tacklers knocked over this play: each gets up when the sim next moves him (slice 10m). */
  private down = new Set<UnitId>();

  constructor(private readonly host: FxHost) {}

  private sprite(u: UnitId): UnitSprite {
    const s = this.host.sprites.get(u);
    if (!s) throw new Error(`no sprite for ${u}`);
    return s;
  }

  play(e: PlayEvent, quiet = false): void {
    // End states first: they hold whether the event was watched or skipped.
    if (e.type === 'hold' && e.pancake) this.flatten(e.rusher);
    if (e.type === 'broken-tackle') this.knockDown(e.by);
    if (e.type === 'move' && this.down.has(e.unit)) this.getUp(e.unit, quiet);
    if (e.type === 'incomplete') this.host.deadBall();
    if (e.type === 'whistle') {
      this.engaged = [];
      this.down.clear();
      this.host.onWhistle(e);
    }
    if (!quiet) this.animate(e);
  }

  private animate(e: PlayEvent): void {
    for (const f of floatsFor(e)) this.float(f);
    switch (e.type) {
      case 'spend':
        this.label(e.unit, this.host.moveName(e.unit, e.moveId).toUpperCase());
        break;
      case 'battle':
        this.spark(e.at, e.matchup.crit !== undefined);
        if (e.matchup.superEffective) this.flash(e.matchup.winner === 'a' ? e.b : e.a);
        if (e.matchup.crit !== undefined) this.shake(0.5);
        break;
      case 'hold':
        if (!e.pancake) this.engaged.push({ a: e.unit, b: e.rusher, until: e.until });
        else this.bigHit([]); // the rusher is already flat; no squash to spring back from
        break;
      case 'shed':
        this.engaged = this.engaged.filter((x) => x.b !== e.unit);
        this.pop(e.unit);
        break;
      case 'pressure':
        this.ring(e.unit, color('accent-red'));
        break;
      case 'open':
        this.ring(e.unit, color('text'));
        this.label(e.unit, `+${e.steps}`);
        break;
      case 'covered':
        this.link(e.unit, e.by);
        break;
      case 'catch':
        this.pop(e.unit);
        break;
      case 'handoff':
        this.pop(e.to);
        break;
      case 'tackle':
        this.bigHit([e.unit, ...e.by]);
        if (e.outOfBounds) this.host.banner('OUT OF BOUNDS');
        break;
      case 'sack':
        this.bigHit([e.unit, e.by]);
        this.host.banner('SACK');
        break;
      case 'incomplete':
        if (e.by) this.pop(e.by);
        this.host.banner('INCOMPLETE');
        break;
      case 'throw-away':
        this.host.banner('THROWN AWAY');
        break;
      case 'interception':
        this.flash(e.unit);
        this.pop(e.unit);
        this.host.banner('INTERCEPTED');
        break;
      case 'fumble':
        this.bigHit(e.with ? [e.unit, e.by, e.with] : [e.unit, e.by]);
        this.ring(e.recoveredBy, color('accent-red'));
        this.host.banner('FUMBLE');
        break;
      case 'broken-tackle':
        this.pop(e.unit);
        this.shake(0.5);
        break;
      default:
        // snap, move and throw are the tracks themselves (formation, legs, the ball's arc).
        break;
    }
  }

  /** A pancaked rusher lies flat for the rest of the play. */
  private flatten(unit: UnitId): void {
    const s = this.sprite(unit);
    s.body.setScale(1.3 * s.scale, 0.5 * s.scale);
  }

  /** A tackler who was run through ends up on his side. */
  private knockDown(unit: UnitId): void {
    this.down.add(unit);
    const body = this.sprite(unit).body;
    if (this.host.reduced) {
      body.setAngle(90);
      return;
    }
    this.host.scene.tweens.add({ targets: body, angle: 90, duration: POP_MS });
  }

  /** A knocked-over tackler the sim sends after the carrier again stands up (slice 10m). */
  private getUp(unit: UnitId, quiet: boolean): void {
    this.down.delete(unit);
    const body = this.sprite(unit).body;
    this.host.scene.tweens.killTweensOf(body);
    if (this.host.reduced || quiet) {
      body.setAngle(0);
      return;
    }
    this.host.scene.tweens.add({ targets: body, angle: 0, duration: POP_MS });
  }

  private shake(fraction: number): void {
    if (this.host.reduced) return;
    const cam = this.host.scene.cameras.main;
    cam.shake(num('t-hit-stop'), (num('shake-px') * this.host.dpr * fraction) / cam.width);
  }

  /** Cosmetic x offset this frame: blockers and rushers wobble a sprite pixel while engaged. */
  jostle(unit: UnitId, t: number): number {
    if (this.host.reduced) return 0;
    const px = this.sprite(unit).scale;
    for (const x of this.engaged) {
      if (t >= x.until) continue;
      if (x.a === unit) return Math.sin(t * 40) > 0 ? px : 0;
      if (x.b === unit) return Math.sin(t * 40) > 0 ? 0 : -px;
    }
    return 0;
  }

  /**
   * A tag near either edge of the view flips inward so it stays on screen (slice 8d: a spend label
   * at the right sideline ran off the canvas). Placed again every frame while it is up: its unit
   * keeps running and the camera keeps following the ball.
   */
  private inward(text: Phaser.GameObjects.Text, s: UnitSprite): void {
    const { scene, dpr } = this.host;
    const pad = Math.round(LABEL_GAP_CSS_PX * dpr);
    const place = () => {
      const view = scene.cameras.main.worldView;
      const a = labelAnchor(s.container.x, text.width, view.x, view.right, pad);
      text.setOrigin(a.originX, text.originY);
      text.x = a.dx;
    };
    place();
    scene.events.on(Phaser.Scenes.Events.POST_UPDATE, place);
    text.once(Phaser.GameObjects.Events.DESTROY, () =>
      scene.events.off(Phaser.Scenes.Events.POST_UPDATE, place),
    );
  }

  /** Whether a tag goes above `s` (see `tagAbove`), offsets from its top and bottom edges. */
  private above(prefer: boolean, s: UnitSprite, up: number, down: number, h: number): boolean {
    const view = this.host.scene.cameras.main.worldView;
    const bottom = view.bottom - this.host.safeInset();
    return tagAbove(prefer, s.container.y, s.top + up, s.bottom + down, h, view.y, bottom);
  }

  private label(unit: UnitId, text: string): void {
    const { scene, reduced, dpr } = this.host;
    const s = this.sprite(unit);
    const gap = Math.round(LABEL_GAP_CSS_PX * dpr);
    const tag = scene.add.text(0, 0, text, {
      fontFamily: '"Press Start 2P"',
      fontSize: `${Math.round(LABEL_CSS_PX * dpr)}px`,
      color: cssColor('text'),
      stroke: cssColor('border-dark'),
      strokeThickness: Math.round(LABEL_STROKE_CSS_PX * dpr),
    });
    // Above offense, below defense, so a blocker's and his rusher's tags never overlap; the other
    // way at the top of the view or over the scorebug.
    const above = this.above(s.side === 'off', s, -gap, gap, tag.height);
    tag.setOrigin(0.5, above ? 1 : 0).setY(above ? s.top - gap : s.bottom + gap);
    this.inward(tag, s);
    s.container.add(tag);
    if (reduced) {
      scene.time.delayedCall(LABEL_MS, () => tag.destroy());
      return;
    }
    scene.tweens.add({
      targets: tag,
      y: tag.y - Math.round(LABEL_RISE_CSS_PX * dpr),
      alpha: 0,
      delay: LABEL_MS / 2,
      duration: LABEL_MS / 2,
      onComplete: () => tag.destroy(),
    });
  }

  /**
   * A floating number (slice 8c) over its Pokémon, riding along with it, rising --float-rise and
   * fading over --t-float. The HP number sits right over the head; the multiplier and CRIT a line
   * above, so they never cover it. Under reduced motion the rise is 0: it shows in place, then
   * goes.
   */
  private float(f: Float): void {
    const { scene, dpr } = this.host;
    const size = Math.round(num('float-size') * dpr);
    const text = scene.add
      .text(0, 0, f.text, {
        fontFamily: '"Press Start 2P"',
        fontSize: `${size}px`,
        color: cssColor(FLOAT_COLOR[f.kind]),
        stroke: cssColor('border-dark'),
        strokeThickness: Math.round(LABEL_STROKE_CSS_PX * dpr),
      })
      .setOrigin(0.5, 1);
    const gap = Math.round(LABEL_GAP_CSS_PX * dpr);
    const s = this.sprite(f.unit);
    const lift = f.kind === 'damage' || f.kind === 'heal' ? 0 : Math.round(size * 1.4);
    // Over the head, or under the feet at the top of the view.
    if (this.above(true, s, -gap - lift, gap + lift, text.height)) {
      text.setPosition(0, s.top - gap - lift);
    } else text.setOrigin(0.5, 0).setPosition(0, s.bottom + gap + lift);
    this.inward(text, s);
    s.container.add(text);
    const ms = num('t-float');
    const rise = Math.round(num('float-rise') * dpr);
    if (rise === 0) {
      scene.time.delayedCall(ms, () => text.destroy());
      return;
    }
    scene.tweens.add({ targets: text, y: text.y - rise, duration: ms });
    scene.tweens.add({
      targets: text,
      alpha: 0,
      delay: ms / 2,
      duration: ms / 2,
      onComplete: () => text.destroy(),
    });
  }

  private spark(at: [number, number], big: boolean): void {
    const { scene, reduced, textures } = this.host;
    if (reduced) return;
    const p = this.host.toPx(at);
    const img = scene.add.image(p.x, p.y, textures.spark).setDepth(5).setScale(0.2);
    scene.tweens.add({
      targets: img,
      scale: big ? 2 : 1,
      alpha: 0,
      duration: POP_MS * 1.5,
      onComplete: () => img.destroy(),
    });
  }

  /** One-frame white flash on a sprite that lost to a super-effective hit. */
  private flash(unit: UnitId): void {
    if (this.host.reduced) return;
    const s = this.sprite(unit);
    s.body.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
    this.host.scene.time.delayedCall(FRAME_MS, () =>
      s.body.setTint(s.tint).setTintMode(Phaser.TintModes.MULTIPLY),
    );
  }

  private pop(unit: UnitId): void {
    if (this.host.reduced) return;
    const s = this.sprite(unit);
    s.body.setScale(1.15 * s.scale);
    this.host.scene.tweens.add({ targets: s.body, scale: s.scale, duration: POP_MS });
  }

  private ring(unit: UnitId, tint: number): void {
    const { scene, reduced, textures } = this.host;
    const s = this.sprite(unit);
    const img = scene.add.image(0, s.mid, textures.ring).setTint(tint);
    s.container.addAt(img, 0);
    if (reduced) {
      scene.time.delayedCall(RING_MS * 2, () => img.destroy());
      return;
    }
    img.setScale(0.6);
    scene.tweens.add({
      targets: img,
      scale: 1.4,
      alpha: 0,
      duration: RING_MS,
      repeat: 1,
      onComplete: () => img.destroy(),
    });
  }

  /** A short line between a covered receiver and the defender sitting on him. */
  private link(a: UnitId, b: UnitId): void {
    const { scene, dpr } = this.host;
    const sa = this.sprite(a);
    const sb = this.sprite(b);
    const g = scene.add.graphics().setDepth(0);
    g.lineStyle(Math.round(LINK_CSS_PX * dpr), color('text-muted'), 0.9);
    g.lineBetween(sa.container.x, sa.container.y + sa.mid, sb.container.x, sb.container.y + sb.mid);
    scene.time.delayedCall(LABEL_MS, () => g.destroy());
  }

  /** Tackle, pancake, strip: hit-stop, camera shake, squash-and-stretch on everyone in it. */
  private bigHit(units: UnitId[]): void {
    const { scene, reduced } = this.host;
    const ms = num('t-hit-stop');
    this.host.hitStop(ms);
    if (reduced || ms === 0) return;
    const cam = scene.cameras.main;
    cam.shake(ms, (num('shake-px') * this.host.dpr) / cam.width);
    for (const u of units) {
      const { body, scale } = this.sprite(u);
      body.setScale(1.25 * scale, 0.75 * scale);
      scene.tweens.add({
        targets: body,
        scaleX: scale,
        scaleY: scale,
        delay: ms,
        duration: POP_MS,
      });
    }
  }
}
